import { faHandWave } from '@fortawesome/pro-duotone-svg-icons'
import { useNavigate } from 'react-router-dom'

import AppLayout from '../../components/app-layout/AppLayout'
import CardSurface from '../../components/card-surface/CardSurface'
import Button from '../../components/button/Button'
import Icon from '../../components/icon/Icon'
import TenancySwitcher from '../../components/tenancy-switcher/TenancySwitcher'
import LeaseRenewalSection from '../../components/lease-renewal/LeaseRenewalSection'
import DepositCard from '../../components/security-deposit/DepositCard'
import MessagesCard from '../../components/resident-messages/MessagesCard'
import { useResidentTenancy } from '../../_hooks/use-resident-tenancy'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { formatCents } from '../../_utilities/resident-format'
import {
  RENT_HANDLED_ELSEWHERE_MESSAGE,
  isRentHandledElsewhere,
  resolveManagedBalanceSummary,
} from '../../_utilities/ledger-keeper'
import {
  RESIDENT_QUICK_ACTIONS,
  RESIDENT_COMING_SOON,
  getResidentGreeting,
} from './helpers'
import {
  PageStack,
  Hero,
  Greeting,
  GreetingSubtitle,
  SectionTitle,
  QuietText,
  BalanceCardRow,
  BalanceText,
  BalanceLabel,
  BalanceAmount,
  QuickActionsGrid,
  QuickActionCard,
  QuickActionIconWrap,
  QuickActionTitle,
  QuickActionSubtitle,
  ComingSoonGrid,
  ComingSoonCard,
  ComingSoonIconWrap,
  ComingSoonTitle,
  ComingSoonPill,
} from './styled'

/**
 * The renter's resident-portal home dashboard. Greets them, shows their
 * residence (with a switcher when they have more than one), a one-line balance
 * summary with a shortcut into Payments, quick actions into the live resident
 * sections, and a teaser of the sections still to come. Tenancy + ledger state
 * come from the shared resident context so nothing re-fetches across surfaces.
 *
 * @returns {JSX.Element}
 */
const MyHomePage = (): JSX.Element => {
  const navigate = useNavigate()
  const { profile } = useQuickPassProfile()
  const {
    isLoadingTenancies,
    hasResidentAccess,
    selectedTenancy,
    ledger,
    isLoadingLedger,
  } = useResidentTenancy()

  const firstName =
    profile !== null && profile.firstName !== '' ? profile.firstName : 'there'
  const greeting = getResidentGreeting()

  let balanceText = '—'
  if (isLoadingLedger === true) {
    balanceText = 'Loading…'
  } else if (ledger !== null) {
    balanceText = formatCents(ledger.balanceCents)
  }

  const hasBalanceOwing = ledger !== null && ledger.balanceCents > 0

  // When the books are kept elsewhere, the balance that system last reported,
  // while it is fresh enough to show as current.
  const managedBalance = resolveManagedBalanceSummary(
    selectedTenancy,
    Date.now(),
  )

  return (
    <AppLayout contentMaxWidth="standard">
      <PageStack>
        <Hero>
          <Greeting>
            {greeting}, {firstName} <Icon icon={faHandWave} label="Wave" />
          </Greeting>
          <GreetingSubtitle>
            Welcome home. Here&rsquo;s your residence at a glance.
          </GreetingSubtitle>
        </Hero>

        {isLoadingTenancies === true ? (
          <CardSurface padding="loose">
            <QuietText>Loading your home…</QuietText>
          </CardSurface>
        ) : null}

        {isLoadingTenancies === false && hasResidentAccess === false ? (
          <CardSurface padding="loose">
            <QuietText>
              You don&rsquo;t have any tenancies yet. When your property manager
              invites you, claim the invite from your email to see your home
              here.
            </QuietText>
          </CardSurface>
        ) : null}

        {hasResidentAccess === true ? (
          <>
            <TenancySwitcher alwaysShow />

            {selectedTenancy !== null &&
            isRentHandledElsewhere(selectedTenancy) ? (
              <CardSurface padding="standard">
                {managedBalance === null ? (
                  <BalanceText>
                    <BalanceLabel>Rent</BalanceLabel>
                    <QuietText>{RENT_HANDLED_ELSEWHERE_MESSAGE}</QuietText>
                  </BalanceText>
                ) : (
                  <BalanceText>
                    <BalanceLabel>{managedBalance.label}</BalanceLabel>
                    <BalanceAmount>{managedBalance.amountText}</BalanceAmount>
                    <QuietText>{managedBalance.noteText}</QuietText>
                  </BalanceText>
                )}
              </CardSurface>
            ) : null}

            {selectedTenancy !== null &&
            isRentHandledElsewhere(selectedTenancy) === false ? (
              <CardSurface padding="standard">
                <BalanceCardRow>
                  <BalanceText>
                    <BalanceLabel>Balance owing</BalanceLabel>
                    <BalanceAmount>{balanceText}</BalanceAmount>
                  </BalanceText>
                  <Button
                    type="button"
                    variant="primary"
                    size="small"
                    onClick={() => navigate('/payments')}
                  >
                    {hasBalanceOwing ? 'Pay rent' : 'View payments'}
                  </Button>
                </BalanceCardRow>
              </CardSurface>
            ) : null}

            {selectedTenancy !== null ? (
              <LeaseRenewalSection tenancyId={selectedTenancy.id} />
            ) : null}

            {selectedTenancy !== null ? (
              <MessagesCard tenancyId={selectedTenancy.id} />
            ) : null}

            {selectedTenancy !== null ? (
              <DepositCard tenancyId={selectedTenancy.id} />
            ) : null}

            <div>
              <SectionTitle>Quick actions</SectionTitle>
              <QuickActionsGrid>
                {RESIDENT_QUICK_ACTIONS.map(action => (
                  <QuickActionCard key={action.to} to={action.to}>
                    <QuickActionIconWrap aria-hidden="true">
                      <Icon icon={action.icon} />
                    </QuickActionIconWrap>
                    <QuickActionTitle>{action.title}</QuickActionTitle>
                    <QuickActionSubtitle>{action.subtitle}</QuickActionSubtitle>
                  </QuickActionCard>
                ))}
              </QuickActionsGrid>
            </div>

            <div>
              <SectionTitle>Coming soon</SectionTitle>
              <ComingSoonGrid>
                {RESIDENT_COMING_SOON.map(item => (
                  <ComingSoonCard key={item.title}>
                    <ComingSoonIconWrap aria-hidden="true">
                      <Icon icon={item.icon} />
                    </ComingSoonIconWrap>
                    <ComingSoonTitle>{item.title}</ComingSoonTitle>
                    <ComingSoonPill>Soon</ComingSoonPill>
                  </ComingSoonCard>
                ))}
              </ComingSoonGrid>
            </div>
          </>
        ) : null}
      </PageStack>
    </AppLayout>
  )
}

export default MyHomePage
